import React, { useEffect } from 'react';
import { Platform, View, type ViewProps } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import { AnimatedView } from './animated';


export interface SlideInViewProps extends ViewProps {
  delay?: number;
  duration?: number;
  direction?: 'left' | 'right' | 'up' | 'down';
  distance?: number;
  className?: string;
  /**
   * Na web, renderiza o conteúdo já visível, sem entrada animada. O export estático
   * grava a opacidade inicial (0) no HTML, e o texto ficaria invisível para
   * buscador e para o LCP até o JavaScript rodar. Ignorado no nativo.
   */
  disableOnWeb?: boolean;
  children: React.ReactNode;
}

export function SlideInView({
  delay = 0,
  duration = 400,
  direction = 'up',
  distance = 20,
  className,
  disableOnWeb = false,
  children,
  style,
  ...props
}: SlideInViewProps) {
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    const initialX = direction === 'left' ? -distance : direction === 'right' ? distance : 0;
    const initialY = direction === 'up' ? distance : direction === 'down' ? -distance : 0;

    translateX.value = initialX;
    translateY.value = initialY;
    opacity.value = 0;

    translateX.value = withDelay(
      delay,
      withTiming(0, {
        duration,
        easing: Easing.out(Easing.cubic),
      }),
    );
    translateY.value = withDelay(
      delay,
      withTiming(0, {
        duration,
        easing: Easing.out(Easing.cubic),
      }),
    );
    opacity.value = withDelay(
      delay,
      withTiming(1, {
        duration,
        easing: Easing.out(Easing.cubic),
      }),
    );
  }, [delay, duration, direction, distance, translateX, translateY, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
  }));

  if (disableOnWeb && Platform.OS === 'web') {
    return (
      <View className={className} style={style} {...props}>
        <View style={{ flexGrow: 1, flexShrink: 1 }}>{children}</View>
      </View>
    );
  }

  // Classes de layout ficam num View puro: className em animated components é
  // ignorado/clobberado no nativo, o que colapsa flex-1/min-h dos filhos.
  return (
    <View className={className} style={style} {...props}>
      <AnimatedView style={[{ flexGrow: 1, flexShrink: 1 }, animatedStyle]}>
        {children}
      </AnimatedView>
    </View>
  );
}
